import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { getAuthHeadersFromUserData } from '../shared/utils/auth-headers.util';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { LoggerService } from './logger.service';
import { PrepaidRedemptionVisit } from '../shared/utils/prepaid-redemption-result.util';

export type { PrepaidRedemptionVisit };

/** One prepaid redemption line for `POST /order` (`wash_type: prepaid_wash_redemption`). */
export interface CreateOrderRedemptionItem {
  purchase_item_id: string;
  package_id: string;
  count: number;
}

/** One add-on line for POST /order (request body). */
export interface CreateOrderAddOnVariantPayload {
  add_on_id: string;
  /** Use empty string when the API has no separate variant id. */
  add_on_variant_id: string;
  add_on_custom_price: number;
  add_on_count: number;
}

export interface CreateOrderPayload {
  customer_id: string;
  /** Omit for `prepaid_wash_book` (API rejects `package_id` for that wash type). */
  package_id?: string;
  location_id: string;
  /** Omit when no wash package / add-on requires a license plate. */
  vehicle_id?: string;
  wash_type:
    | 'single_wash'
    | 'monthly_membership'
    | 'add_on'
    | 'prepaid_wash_book'
    | 'prepaid_wash_redemption';
  /** Required when `wash_type` is `prepaid_wash_book`. */
  prepaid_wash_book_id?: string;
  /** Required when `wash_type` is `prepaid_wash_redemption`. */
  prepaid_purchase_id?: string;
  /** One or more lines to redeem in a single `POST /order` request. */
  redemption_items?: CreateOrderRedemptionItem[];
  voucher_code?: string;
  appointment_time: string;
  source: 'web' | 'mobile' | 'api';
  order_source: 'lpr' | 'saas';
  final_price?: string | number;
  /** Package line list price (catalog or custom single-wash from checkout UI). */
  list_price?: string | number;
  /** Shop discount UUID, or custom discount UUID once persisted. */
  discount_id?: string;
  /** Applied discount amount (dollars). */
  discount?: number;
  /** Selected add-on lines with prices and counts. */
  add_on_variants?: CreateOrderAddOnVariantPayload[];
  /**
   * LPR entry id that triggered this order (the notification's own entry id).
   * When set, the backend links the order to this exact entry inside the order
   * creation transaction, so identity is never re-derived from the plate string.
   */
  lpr_entry_id?: string;
  /**
   * Optional lane that produced the order (LPR / lane event).
   * Counter orders may omit this and attach a lane later via order update.
   */
  lane_id?: string;
  /** CWS-1881: who redeemed a prepaid wash, recorded on the redemption row. */
  redeemer_customer_id?: string;
  redeemer_first_name?: string;
  redeemer_last_name?: string;
  redeemer_phone?: string;
  redeemer_email?: string;
  redeemer_plate?: string;
}

/** Add-on line returned with POST /order under `data.add_ons` (legacy: `add_on_variants`). */
export interface CreateOrderAddOnVariantRow {
  id?: string;
  order_id?: string;
  addon_id?: string;
  variant_id?: string;
  custom_price?: number;
  add_on_count?: number;
  add_on_name?: string;
  add_on_variant_name?: string;
  price?: string | number;
  amount?: string | number;
  line_total?: string | number;
}

/** Discount object returned with POST /order. */
export interface CreateOrderDiscountRow {
  id?: string;
  name?: string;
  code?: string;
  discount_type?: string;
  discount_value?: string | number;
  type?: string;
  is_active?: boolean;
}

/** CWS-1945: POST /order/checkout — exactly one of `order_id` / `payload`. */
export interface CheckoutOrderRequest {
  /** One UUID per checkout on this tablet; reused for retries so they pay the same order. */
  request_id: string;
  payment_method: string;
  order_id?: string;
  payload?: CreateOrderPayload;
}

export interface CheckoutOrderResponse extends CreateOrderResponse {
  data: CreateOrderResponse['data'] & {
    /** True when this call created the order. */
    created?: boolean;
    /** True when it paid the unpaid order the LPR read was already linked to. */
    reused?: boolean;
  };
}

/** `error.data.code` on a 409 from POST /order/checkout. */
export type CheckoutOrderRefusalCode =
  | 'ORDER_ALREADY_PAID'
  | 'ORDER_ALREADY_COMPLETED'
  | 'ORDER_CANCELLED'
  | 'PAYMENT_IN_PROGRESS'
  | 'CHECKOUT_ORDER_CHANGED';

/**
 * CWS-1882 ("one sale = one order"): a `prepaid_wash_redemption` request creates NO order
 * any more. The response's `data.order` is the PURCHASE order (`prepaid_wash_book`), and
 * `order.prepaid_redemption` describes the visit that was recorded against it.
 */
export interface CreatedOrderRow {
  id: string;
  order_id: string;
  customer_id: string;
  user_id: string;
  package_id: string;
  location_id: string;
  vehicle_id: string;
  wash_type: string;
  list_price: string;
  discount: string;
  voucher_code?: string;
  final_price: string;
  appointment_time: string;
  source: string;
  order_status: string;
  payment_status: string;
  stripe_payment_intent_id?: string;
  payment_intent_client_secret?: string;
  stripe_account_id?: string;
  status: boolean;
  cancelled_by?: string;
  cancellation_reason?: string;
  cancelled_at?: string;
  created_at: string;
  updated_at: string;
  package_name?: string;
  make?: string;
  model?: string;
  year?: number;
  license_plate?: string;
  discount_id?: string;
  fleet_id?: string | null;
  fleet_lp_id?: string | null;
  /** Present only on the answer to a `prepaid_wash_redemption` request (CWS-1882). */
  prepaid_redemption?: PrepaidRedemptionVisit | null;
  /** Standalone `add_on` order when add-ons were sold alongside a redemption (CWS-1882). */
  addon_order?: CreatedOrderRow | null;
}

export interface CreateOrderResponse {
  status: boolean;
  status_code: number;
  message: string;
  data: {
    order: CreatedOrderRow;
    discount?: CreateOrderDiscountRow;
    /** Current API: persisted add-ons on the order. */
    add_ons?: CreateOrderAddOnVariantRow[];
    /** @deprecated Prefer `add_ons`; kept for older API responses. */
    add_on_variants?: CreateOrderAddOnVariantRow[];
    client_secret?: string;
  };
}

/**
 * Service responsible for creating and validating orders, formatting appointment times, and determining wash types. Provides API integration for order creation and utility helpers used by components and other services.
 * @component
 * @selector order-creation-service
 * @example
 *   <order-creation-service [payload]="samplePayload" (createOrder)="onOrderCreated($event)"></order-creation-service>
 * @input CreateOrderPayload payload - Payload object containing customer_id, package_id, location_id, vehicle_id, wash_type, appointment_time and source; used by createOrder method to submit a new order.
 * @output Observable<CreateOrderResponse> createOrder - Returns an observable that emits the server response when a new order is created; subscribe to handle success or error responses.
 *
 * Key details:
 * - Provided in root: This service is decorated with @Injectable({ providedIn: 'root' }) and is available application-wide.
 * - Dependencies: Injects HttpClient for API calls and LoggerService for logging API interactions.
 *
 * Key methods:
 * - createOrder(payload: CreateOrderPayload): Observable<CreateOrderResponse>
 *   Makes a POST request to the /order endpoint with authentication headers and logs the API call. Returns an observable of the created order response.
 * - validateOrderPayload(payload: CreateOrderPayload): { isValid: boolean; errors: string[] }
 *   Performs synchronous validation of required fields (customer_id, package_id, location_id, vehicle_id, wash_type, appointment_time, source) and returns validation state and error messages.
 * - formatAppointmentTime(date: Date, time: string): string
 *   Combines a Date and time string (HH:mm) into an ISO 8601 timestamp suitable for the API.
 * - getWashType(packageType: string): 'single_wash' | 'monthly_membership'
 *   Maps package type strings to normalized wash type identifiers used by the backend.
 * - (private) getHeaders(): HttpHeaders
 *   Builds authorization and JSON content-type headers from stored user data.
 *
 * Important notes / lifecycle:
 * - There are no Angular lifecycle hooks for services (ngOnInit etc.); initialization occurs in the constructor.
 * - createOrder logs request details to the console for debugging and uses LoggerService.logApiCall to record the API interaction.
 * - Validation is performed client-side via validateOrderPayload; callers should check the isValid flag before invoking createOrder to avoid unnecessary API calls.
 */
@Injectable({
  providedIn: 'root',
})
export class OrderCreationService {
  private readonly baseUrl = `${environment.baseUrl}/${environment.apiVersion}`;

  constructor(
    private http: HttpClient,
    private logger: LoggerService
  ) {}

  private getHeaders() {
    return getAuthHeadersFromUserData({ includeJsonContentType: true });
  }

  /**
   * Create a new order
   */
  createOrder(payload: CreateOrderPayload): Observable<CreateOrderResponse> {
    const url = `${this.baseUrl}/order`;
    const headers = this.getHeaders();
    const body = this.sanitizeCreateOrderPayload(payload);

    // Log the request details for debugging
    this.logger.debug('🔍 Order Creation Request:', {
      url,
      headers: headers.keys().map(key => `${key}: ${headers.get(key)}`),
      payload: body,
    });

    this.logger.logApiCall('POST', url);

    return this.http.post<CreateOrderResponse>(url, body, { headers });
  }

  /**
   * CWS-1945: the pay action. Creates the order now (or reuses the unpaid one for the same read)
   * and claims it for this payment attempt, so a second tablet cannot pay it too. Send
   * `order_id` to pay an order that already exists, or the create-order payload for a draft.
   * A losing attempt gets a 409 whose `data.code` says why (see CheckoutOrderRefusalCode).
   */
  checkoutOrder(request: CheckoutOrderRequest): Observable<CheckoutOrderResponse> {
    const url = `${this.baseUrl}/order/checkout`;
    const { request_id, payment_method, order_id, payload } = request;
    const body = order_id
      ? { request_id, payment_method, order_id }
      : {
          ...this.sanitizeCreateOrderPayload(payload as CreateOrderPayload),
          request_id,
          payment_method,
        };
    this.logger.logApiCall('POST', url);
    return this.http.post<CheckoutOrderResponse>(url, body, { headers: this.getHeaders() });
  }

  /** Backend disallows `package_id` when `wash_type` is `prepaid_wash_book` (even empty string). */
  private sanitizeCreateOrderPayload(payload: CreateOrderPayload): CreateOrderPayload {
    const body = { ...payload };
    const laneId = body.lane_id != null ? String(body.lane_id).trim() : '';
    if (laneId) {
      body.lane_id = laneId;
    } else {
      delete body.lane_id;
    }
    if (body.wash_type === 'prepaid_wash_book' || body.wash_type === 'prepaid_wash_redemption') {
      delete body.package_id;
    }
    if (body.wash_type === 'prepaid_wash_redemption') {
      delete (body as Record<string, any>)['redemption_token'];
      delete (body as Record<string, any>)['prepaid_redemption_items'];
      if (body.redemption_items?.length) {
        body.redemption_items = body.redemption_items
          .map(line => ({
            purchase_item_id: String(line.purchase_item_id ?? '').trim(),
            package_id: String(line.package_id ?? '').trim(),
            count: Math.max(1, Math.floor(Number(line.count)) || 1),
          }))
          .filter(line => line.purchase_item_id && line.package_id);
      }
    }
    if (body.wash_type !== 'prepaid_wash_redemption') {
      const pkg = body.package_id != null ? String(body.package_id).trim() : '';
      if (pkg) {
        body.package_id = pkg;
      } else {
        delete body.package_id;
      }
    }
    return body;
  }

  /**
   * Validate order payload before creation
   */
  validateOrderPayload(
    payload: CreateOrderPayload,
    options?: { requirePackage?: boolean; requireVehicle?: boolean }
  ): { isValid: boolean; errors: string[] } {
    const requireVehicle = options?.requireVehicle !== false;
    const hasAddons = (payload.add_on_variants?.length ?? 0) > 0;
    const hasPackage = !!(payload.package_id && String(payload.package_id).trim());
    const hasPrepaidWashBook =
      payload.wash_type === 'prepaid_wash_book' &&
      !!(payload.prepaid_wash_book_id && String(payload.prepaid_wash_book_id).trim());
    const hasPrepaidRedemption =
      payload.wash_type === 'prepaid_wash_redemption' &&
      (payload.redemption_items?.length ?? 0) > 0 &&
      !!(payload.prepaid_purchase_id && String(payload.prepaid_purchase_id).trim());
    const errors: string[] = [];

    if (!payload.customer_id) {
      errors.push('Customer ID is required');
    }

    if (!hasPackage && !hasAddons && !hasPrepaidWashBook && !hasPrepaidRedemption) {
      errors.push('Select a package, prepaid wash book, redemption, or at least one add-on');
    }

    if (!payload.location_id) {
      errors.push('Location ID is required');
    }

    if (requireVehicle && !payload.vehicle_id) {
      errors.push('Vehicle ID is required');
    }

    if (!payload.wash_type) {
      errors.push('Wash type is required');
    }

    if (!payload.appointment_time) {
      errors.push('Appointment time is required');
    }

    if (!payload.source) {
      errors.push('Source is required');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Format appointment time to ISO string
   */
  formatAppointmentTime(date: Date, time: string): string {
    const [hours, minutes] = time.split(':');
    const appointmentDate = new Date(date);
    appointmentDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
    return appointmentDate.toISOString();
  }

  /**
   * Determine wash type from package option
   */
  getWashType(packageType: string): 'single_wash' | 'monthly_membership' {
    switch (packageType.toLowerCase()) {
      case 'single':
      case 'single_wash':
        return 'single_wash';
      case 'monthly':
      case 'monthly_membership':
      case 'family':
        return 'monthly_membership';
      default:
        return 'single_wash';
    }
  }
}
